import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'

import type { AttendeeUpdate, MyAttendee, MyOrder } from './types'

/**
 * « Mon Masked Night ».
 *
 * L'accès repose sur le jeton reçu à la commande — pas de compte, pas de mot
 * de passe à retrouver le soir de la fête. Le jeton reste dans l'URL du lien
 * personnel ; c'est lui qu'il faut garder.
 */
export function useMyOrder(token: string | null) {
  return useQuery({
    queryKey: ['my-order', token],
    queryFn: () => apiClient.get<MyOrder>(`/me/order?token=${encodeURIComponent(token ?? '')}`),
    enabled: Boolean(token),
    // Le statut de paiement peut changer pendant qu'on regarde la page.
    refetchInterval: (query) => (query.state.data?.paid ? false : 15_000),
  })
}

/**
 * URL de l'image du QR d'entrée — un seul par commande.
 *
 * Le navigateur la charge comme n'importe quelle image, et la garde en cache
 * hors ligne une fois affichée : à la porte, le réseau est saturé par trois
 * cents téléphones.
 */
export function orderQrImageUrl(token: string): string {
  const prefix = import.meta.env.VITE_API_PREFIX ?? '/api/v1'
  return `${prefix}/me/order/qr.png?token=${encodeURIComponent(token)}`
}

export function useUpdateAttendee(token: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ attendeeId, patch }: { attendeeId: string; patch: AttendeeUpdate }) =>
      apiClient.patch<MyAttendee>(
        `/me/attendees/${attendeeId}?token=${encodeURIComponent(token ?? '')}`,
        patch,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-order', token] })
    },
  })
}
